import { Alert, Badge, Skeleton } from "@hakistudio/hakiui";
import { NOTE_COLORS, type NoteColor, type RichDoc } from "@sebas/core";
import { ArrowLeft, Archive, ImagePlus, Mic, Pin, Share2, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FridgePanel } from "@/components/kitchen/FridgePanel";
import { IngredientsPanel } from "@/components/kitchen/IngredientsPanel";
import { CommentsPanel } from "@/components/notes/CommentsPanel";
import { ShareDialog } from "@/components/notes/ShareDialog";
import { AudioRecorder } from "@/components/editor/AudioRecorder";
import { NoteEditor, type NoteEditorHandle } from "@/components/editor/NoteEditor";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { useDeleteNote, useNote, useUpdateNote } from "@/lib/queries";

const TILE_BG: Record<NoteColor, string> = {
  sand: "bg-tile-sand", coral: "bg-tile-coral", amber: "bg-tile-amber",
  mint: "bg-tile-mint", sky: "bg-tile-sky", lilac: "bg-tile-lilac",
};

type SaveState = "idle" | "saving" | "saved" | "conflict" | "error";

export function NotePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isPending, error } = useNote(id);
  const update = useUpdateNote();
  const remove = useDeleteNote();

  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [title, setTitle] = useState("");
  // The version we believe the server holds. Updated from every save response.
  const version = useRef<number>(1);
  const seeded = useRef(false);
  const editorRef = useRef<NoteEditorHandle>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // The mic button on the notes list deep-links here with ?record=1.
  const [recording, setRecording] = useState(searchParams.get("record") === "1");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (data?.note && !seeded.current) {
      setTitle(data.note.title);
      version.current = data.note.version;
      seeded.current = true;
    }
  }, [data]);

  useEffect(() => {
    if (data?.note) version.current = data.note.version;
  }, [data?.note?.version]);

  /**
   * Edits accumulate here between saves.
   *
   * The debounce keeps only the most recent invocation, so passing the patch as
   * an argument meant a title edit followed quickly by a body edit dropped the
   * title entirely. Merging into a ref makes every field survive the coalescing.
   */
  const pending = useRef<{ title?: string; content?: RichDoc }>({});

  const flush = useCallback(() => {
    if (!id) return;
    const patch = pending.current;
    pending.current = {};
    if (Object.keys(patch).length === 0) return;

    setSaveState("saving");
    update.mutate(
      { id, ...patch, ...(patch.content ? { version: version.current } : {}) },
      {
        onSuccess: (res) => {
          version.current = res.note.version;
          setSaveState("saved");
        },
        onError: (err) =>
          setSaveState(err instanceof ApiError && err.status === 409 ? "conflict" : "error"),
      },
    );
  }, [id, update]);

  const debounced = useDebouncedCallback(flush, 800);

  /** Queues a field change; fields merge rather than replace one another. */
  const queueSave = useCallback(
    (patch: { title?: string; content?: RichDoc }) => {
      pending.current = { ...pending.current, ...patch };
      debounced.run();
    },
    [debounced],
  );
  const canEdit = data?.access === "editor" || data?.access === "owner";

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton height="3rem" width="60%" radius="lg" />
        <Skeleton height="14rem" radius="lg" />
      </div>
    );
  }
  if (error || !data) return <Alert variant="danger" title="Could not open this note." />;
  const note = data.note;
  // The fridge is an inventory tool, not a page to write on — it drops the
  // colour swatches and the voice note, and leads with the stock list.
  const isFridge = note.noteType === "fridge";

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/notes")}>
          <ArrowLeft className="h-4 w-4" /> Notes
        </Button>

        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-xs",
              saveState === "conflict" || saveState === "error" ? "text-tile-coral" : "text-ink-faint",
            )}
          >
            {saveState === "saving" && "Saving…"}
            {saveState === "saved" && "Saved"}
            {saveState === "error" && "Could not save"}
            {saveState === "conflict" && "Changed elsewhere"}
          </span>

          {canEdit ? (
            <>
              <Button
                variant={note.isPinned ? "primary" : "secondary"}
                size="icon"
                title={note.isPinned ? "Unpin" : "Pin"}
                onClick={() => update.mutate({ id: note.id, isPinned: !note.isPinned })}
              >
                <Pin className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                title={note.isArchived ? "Unarchive" : "Archive"}
                onClick={() => update.mutate({ id: note.id, isArchived: !note.isArchived })}
              >
                <Archive className="h-4 w-4" />
              </Button>
              <Button variant="secondary" size="icon" title="Share" onClick={() => setSharing(true)}>
                <Share2 className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                title="Delete"
                onClick={() => {
                  if (confirm("Delete this note? This cannot be undone.")) {
                    remove.mutate(note.id, { onSuccess: () => navigate("/notes") });
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Badge size="sm" variant="soft" radius="full">Read only</Badge>
          )}
        </div>
      </div>

      {saveState === "conflict" ? (
        <Alert variant="warning" title="Someone else edited this note">
          <span className="flex items-center gap-3">
            Reload to get their changes.
            <button className="font-semibold underline" onClick={() => window.location.reload()}>
              Reload
            </button>
          </span>
        </Alert>
      ) : null}

      <input
        value={title}
        readOnly={!canEdit}
        placeholder="Untitled"
        onChange={(e) => {
          setTitle(e.target.value);
          queueSave({ title: e.target.value });
        }}
        className="font-display w-full bg-transparent text-4xl font-semibold text-ink placeholder:text-ink-faint focus:outline-none lg:text-5xl"
      />

      {isFridge ? <FridgePanel teamId={note.teamId} canEdit={canEdit} /> : null}

      {canEdit && !isFridge ? (
        <div className="flex gap-2">
          {NOTE_COLORS.map((c) => (
            <button
              key={c}
              aria-label={`Colour ${c}`}
              onClick={() => update.mutate({ id: note.id, color: c })}
              className={cn(
                "h-6 w-6 rounded-full ring-offset-2 ring-offset-surface transition",
                TILE_BG[c],
                note.color === c && "ring-2 ring-ink",
              )}
            />
          ))}
        </div>
      ) : null}

      {canEdit && !isFridge ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
            <ImagePlus className="h-4 w-4" /> Image
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setRecording(true)}>
            <Mic className="h-4 w-4" /> Voice note
          </Button>
          <span className="text-xs text-ink-faint">or paste and drop straight into the note</span>
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length) editorRef.current?.insertImageFiles(files);
              e.target.value = "";
            }}
          />
        </div>
      ) : null}

      {note.noteType === "recipe" ? (
        <IngredientsPanel noteId={note.id} canEdit={canEdit} />
      ) : null}

      {recording && id ? (
        <AudioRecorder
          noteId={id}
          onRecorded={(attachmentId, durationMs) => {
            editorRef.current?.insertAudio(attachmentId, durationMs);
            setRecording(false);
            searchParams.delete("record");
            setSearchParams(searchParams, { replace: true });
          }}
          onCancel={() => {
            setRecording(false);
            searchParams.delete("record");
            setSearchParams(searchParams, { replace: true });
          }}
        />
      ) : null}

      {uploadError ? <Alert variant="danger" title={uploadError} /> : null}

      {isFridge ? (
        <p className="px-1 pt-1 text-xs font-medium uppercase tracking-wide text-ink-faint">
          Notes
        </p>
      ) : null}

      <NoteEditor
        initialContent={note.content}
        editable={canEdit}
        noteId={id!}
        handleRef={editorRef}
        onUploadError={setUploadError}
        onChange={(doc) => queueSave({ content: doc })}
      />

      <CommentsPanel noteId={note.id} canModerate={canEdit} currentUserId={note.ownerId} />

      {sharing ? <ShareDialog noteId={note.id} onClose={() => setSharing(false)} /> : null}
    </div>
  );
}
